import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import type { InventoryLotListRow } from "@/modules/inventory/domain/types";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { Loader } from "@/shared/ui/Loader";

import { useNavigate, useSearch } from "@tanstack/react-router";
import { useLotsQuery, useLotsSummaryQuery } from "../api/queries";
import { LotsFilter } from "../ui/LotsFilter";
import { LotsSummaryCards } from "../ui/LotsSummaryCards";
import { LotsTable } from "../ui/LotsTable";
import { LotsToolbar } from "../ui/LotsToolbar";

type LotsSearch = {
  branchId?: string;
  itemType?: "INGREDIENT" | "RETAIL_VARIANT";
  itemId?: string;
  ingredientCategoryId?: string;
  sort?: "fefo" | "fifo";
  sortField?: string;
  sortOrder?: "asc" | "desc";
  offset?: number;
  limit?: number;
};

export function LotsPage() {
  const nav = useNavigate({ from: "/app/inventory/lots" });
  const search = useSearch({ from: "/app/inventory/lots" }) as LotsSearch;

  const params = {
    branchId: search.branchId,
    itemType: search.itemType,
    itemId: search.itemId,
    ingredientCategoryId: search.ingredientCategoryId,
    sort: search.sort ?? "fefo",
    sortField: search.sortField,
    sortOrder: search.sortOrder,
    limit: search.limit ?? 20,
    offset: search.offset ?? 0,
  };

  const summaryParams = {
    branchId: params.branchId,
    itemType: params.itemType,
    itemId: params.itemId,
    ingredientCategoryId: params.ingredientCategoryId,
  };
  const { data, isLoading } = useLotsQuery(params);
  const summaryQuery = useLotsSummaryQuery(summaryParams);
  const items = data?.items ?? [];
  const totalCount = data?.total ?? 0;
  const summary = summaryQuery.data;

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            {
              label: "ລາຍການ Lot",
              to: "/app/inventory/lots",
            },
          ]}
        />
      </Header>
      <Main>
        <LotsToolbar />

        <div className="mb-6">
          <LotsSummaryCards
            expiredCount={summary?.expiredCount ?? 0}
            nearExpiryCount={summary?.nearExpiryCount ?? 0}
            healthyCount={summary?.healthyCount ?? 0}
            isLoading={summaryQuery.isLoading}
          />
        </div>

        <div className="flex flex-col rounded-xl border bg-card pt-2">
          <LotsFilter />

          {isLoading && items.length === 0 ? (
            <div className="flex justify-center py-12">
              <Loader />
            </div>
          ) : (
            <LotsTable
              data={items}
              isLoading={isLoading}
              offset={params.offset}
              limit={params.limit}
              totalCount={totalCount}
              sortBy={params.sortField}
              sortOrder={params.sortOrder}
              onSortingChange={(id, desc) =>
                nav({
                  search: {
                    ...search,
                    sortField: id,
                    sortOrder: desc ? "desc" : "asc",
                  },
                })
              }
              onPaginationChange={(newOffset, newLimit) =>
                nav({
                  search: {
                    ...search,
                    offset: newOffset,
                    limit: newLimit,
                  },
                })
              }
              onViewMovements={(row: InventoryLotListRow) =>
                nav({
                  to: "/app/inventory/lots/$id/movements",
                  params: { id: row.id },
                })
              }
            />
          )}
        </div>
      </Main>
    </>
  );
}
